import { useState, useEffect } from 'react';
import { valuationAPI } from '../services/api';
import './Brands.css';

function Valuation() {
  const [vehicleType, setVehicleType] = useState('car');
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [variants, setVariants] = useState([]);
  const [cities, setCities] = useState([]);
  const [years, setYears] = useState([]);

  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    manufacturerId: '',
    modelId: '',
    variantId: '',
    yearOfPurchase: '',
    kilometersDriven: '',
    city: '',
    ownerNumber: '1',
    originalPrice: ''
  });

  const [valuation, setValuation] = useState(null);

  // Generate years list
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const yearsList = [];
    for (let y = currentYear; y >= currentYear - 20; y--) {
      yearsList.push(y);
    }
    setYears(yearsList);
  }, []);

  // Fetch manufacturers when vehicle type changes
  useEffect(() => {
    fetchManufacturers();
    fetchCities();
    // Reset form when vehicle type changes
    setFormData(prev => ({
      ...prev,
      manufacturerId: '',
      modelId: '',
      variantId: '',
      originalPrice: ''
    }));
    setModels([]);
    setVariants([]);
    setValuation(null);
  }, [vehicleType]);

  // Fetch models when manufacturer changes
  useEffect(() => {
    if (formData.manufacturerId) {
      fetchModels();
    } else {
      setModels([]);
      setVariants([]);
    }
    setFormData(prev => ({ ...prev, modelId: '', variantId: '', originalPrice: '' }));
    setValuation(null);
  }, [formData.manufacturerId]);

  // Fetch variants when model changes
  useEffect(() => {
    if (formData.modelId) {
      fetchVariants();
    } else {
      setVariants([]);
    }
    setFormData(prev => ({ ...prev, variantId: '', originalPrice: '' }));
    setValuation(null);
  }, [formData.modelId]);

  // Update original price when variant is selected
  useEffect(() => {
    if (formData.variantId) {
      const selectedVariant = variants.find(v => v.id.toString() === formData.variantId);
      if (selectedVariant?.price) {
        const priceStr = selectedVariant.price || selectedVariant.onRoadPrice || '';
        setFormData(prev => ({ ...prev, originalPrice: priceStr }));
      }
    }
  }, [formData.variantId, variants]);

  const fetchManufacturers = async () => {
    setLoading(true);
    try {
      const response = vehicleType === 'car'
        ? await valuationAPI.getCarManufacturers()
        : await valuationAPI.getBikeManufacturers();
      setManufacturers(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch manufacturers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    setLoading(true);
    try {
      const response = vehicleType === 'car'
        ? await valuationAPI.getCarModels({ brandId: formData.manufacturerId })
        : await valuationAPI.getBikeModels({ brandId: formData.manufacturerId });
      setModels(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch models');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVariants = async () => {
    setLoading(true);
    try {
      const response = vehicleType === 'car'
        ? await valuationAPI.getCarVariants({ modelId: formData.modelId })
        : await valuationAPI.getBikeVariants({ modelId: formData.modelId });
      setVariants(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch variants');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await valuationAPI.getCities();
      const d = response.data;
      setCities(Array.isArray(d) ? d : (d?.data || []));
    } catch (err) {
      console.error('Failed to fetch cities:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setValuation(null);
  };

  const handleCalculate = async (e) => {
    e.preventDefault();

    if (!formData.yearOfPurchase || !formData.kilometersDriven) {
      setError('Please fill in year of purchase and kilometers driven');
      return;
    }

    setCalculating(true);
    setError('');
    setValuation(null);

    try {
      const payload = {
        variantId: formData.variantId || undefined,
        originalPrice: formData.originalPrice ? parsePrice(formData.originalPrice) : undefined,
        yearOfPurchase: parseInt(formData.yearOfPurchase),
        kilometersDriven: parseInt(formData.kilometersDriven),
        city: formData.city || undefined,
        ownerNumber: parseInt(formData.ownerNumber)
      };

      const response = vehicleType === 'car'
        ? await valuationAPI.calculateCarValuation(payload)
        : await valuationAPI.calculateBikeValuation(payload);

      if (response.data.success) {
        setValuation(response.data.valuation);
      } else {
        setError(response.data.error || 'Failed to calculate valuation');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to calculate valuation');
      console.error(err);
    } finally {
      setCalculating(false);
    }
  };

  // Parse price string to number
  const parsePrice = (priceStr) => {
    if (!priceStr) return 0;
    if (typeof priceStr === 'number') return priceStr;

    let cleanPrice = priceStr.replace(/[₹,\s]/g, '').toLowerCase();

    if (cleanPrice.includes('lakh') || cleanPrice.includes('lac')) {
      const num = parseFloat(cleanPrice.replace(/[^\d.]/g, ''));
      return num * 100000;
    }
    if (cleanPrice.includes('crore') || cleanPrice.includes('cr')) {
      const num = parseFloat(cleanPrice.replace(/[^\d.]/g, ''));
      return num * 10000000;
    }

    return parseFloat(cleanPrice.replace(/[^\d.]/g, '')) || 0;
  };

  const resetForm = () => {
    setFormData({
      manufacturerId: '',
      modelId: '',
      variantId: '',
      yearOfPurchase: '',
      kilometersDriven: '',
      city: '',
      ownerNumber: '1',
      originalPrice: ''
    });
    setModels([]);
    setVariants([]);
    setValuation(null);
    setError('');
  };

  const conditionColors = {
    excellent: { bg: '#dcfce7', border: '#22c55e', text: '#15803d' },
    good: { bg: '#dbeafe', border: '#3b82f6', text: '#1d4ed8' },
    fair: { bg: '#fef3c7', border: '#f59e0b', text: '#b45309' },
    poor: { bg: '#fee2e2', border: '#ef4444', text: '#dc2626' }
  };

  return (
    <div className="brands-page">
      <div className="page-header">
        <h1>Vehicle Resell Valuation</h1>
        <p className="page-subtitle">Calculate the resale value of cars and bikes</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="valuation-container">
        {/* Input Form */}
        <div className="valuation-form-card">
          <h3>Enter Vehicle Details</h3>

          {/* Vehicle Type Toggle */}
          <div className="vehicle-type-toggle">
            <button
              className={`toggle-btn ${vehicleType === 'car' ? 'active' : ''}`}
              onClick={() => setVehicleType('car')}
            >
              Car
            </button>
            <button
              className={`toggle-btn ${vehicleType === 'bike' ? 'active' : ''}`}
              onClick={() => setVehicleType('bike')}
            >
              Bike
            </button>
          </div>

          <form onSubmit={handleCalculate}>
            {/* Manufacturer */}
            <div className="form-group">
              <label>Manufacturer *</label>
              <select
                name="manufacturerId"
                value={formData.manufacturerId}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="">Select Manufacturer</option>
                {manufacturers.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>

            {/* Model */}
            <div className="form-group">
              <label>Model *</label>
              <select
                name="modelId"
                value={formData.modelId}
                onChange={handleInputChange}
                disabled={!formData.manufacturerId || loading}
              >
                <option value="">Select Model</option>
                {models.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>

            {/* Variant */}
            <div className="form-group">
              <label>Variant (Optional)</label>
              <select
                name="variantId"
                value={formData.variantId}
                onChange={handleInputChange}
                disabled={!formData.modelId || loading}
              >
                <option value="">Select Variant</option>
                {variants.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.name} {v.price ? `- ${v.price}` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Year of Purchase */}
            <div className="form-group">
              <label>Year of Purchase *</label>
              <select
                name="yearOfPurchase"
                value={formData.yearOfPurchase}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Year</option>
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            {/* Kilometers Driven */}
            <div className="form-group">
              <label>Kilometers Driven *</label>
              <input
                type="number"
                name="kilometersDriven"
                value={formData.kilometersDriven}
                onChange={handleInputChange}
                placeholder="e.g., 45000"
                min="0"
                required
              />
            </div>

            {/* City */}
            <div className="form-group">
              <label>City</label>
              <select
                name="city"
                value={formData.city}
                onChange={handleInputChange}
              >
                <option value="">Select City</option>
                {cities.map(c => (
                  <option key={c.id} value={c.name}>
                    {c.name} {c.isPopular ? '*' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Owner Number */}
            <div className="form-group">
              <label>Owner Number</label>
              <select
                name="ownerNumber"
                value={formData.ownerNumber}
                onChange={handleInputChange}
              >
                <option value="1">1st Owner</option>
                <option value="2">2nd Owner</option>
                <option value="3">3rd Owner</option>
                <option value="4">4th Owner or more</option>
              </select>
            </div>

            {/* Original Price (Manual Override) */}
            <div className="form-group">
              <label>Original Price - Optional override</label>
              <input
                type="text"
                name="originalPrice"
                value={formData.originalPrice}
                onChange={handleInputChange}
                placeholder="e.g., 8.5 Lakh or 850000"
              />
              <small className="form-hint">Leave empty to use database price</small>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={calculating}>
                {calculating ? 'Calculating...' : 'Calculate Valuation'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Reset
              </button>
            </div>
          </form>
        </div>

        {/* Valuation Result */}
        <div className="valuation-result-card">
          <h3>Valuation Result</h3>

          {!valuation && !calculating && (
            <div className="empty-result">
              <div className="empty-icon">Rs</div>
              <p>Enter vehicle details and click "Calculate Valuation" to see the estimated resale value for all conditions.</p>
            </div>
          )}

          {calculating && (
            <div className="calculating">
              <div className="spinner"></div>
              <p>Calculating valuation...</p>
            </div>
          )}

          {valuation && valuation.conditionPrices && (
            <div className="result-content">
              {/* All Condition Prices */}
              <div className="condition-prices-grid">
                {Object.entries(valuation.conditionPrices).map(([key, data]) => (
                  <div
                    key={key}
                    className="condition-card"
                    style={{
                      backgroundColor: conditionColors[key].bg,
                      borderColor: conditionColors[key].border
                    }}
                  >
                    <div className="condition-header" style={{ color: conditionColors[key].text }}>
                      <span className="condition-label">{data.label}</span>
                      <span className="condition-factor">{data.factor}x</span>
                    </div>
                    <div className="condition-price">{data.formattedPrice}</div>
                    <div className="condition-range">{data.formattedRange}</div>
                    <div className="condition-desc">{data.description}</div>
                  </div>
                ))}
              </div>

              <div className="depreciation-info">
                <div className="depreciation-bar">
                  <div
                    className="depreciation-fill"
                    style={{ width: `${Math.min(valuation.depreciation.totalDepreciation, 100)}%` }}
                  ></div>
                </div>
                <span className="depreciation-text">
                  {valuation.depreciation.totalDepreciation}% total depreciation (based on Good condition)
                </span>
              </div>

              <div className="factors-grid">
                <div className="factor-item">
                  <span className="factor-label">Original Price</span>
                  <span className="factor-value">Rs {valuation.factors.originalPrice?.toLocaleString('en-IN')}</span>
                </div>
                <div className="factor-item">
                  <span className="factor-label">Vehicle Age</span>
                  <span className="factor-value">{valuation.factors.vehicleAge} years</span>
                </div>
                <div className="factor-item">
                  <span className="factor-label">Kilometers</span>
                  <span className="factor-value">{valuation.factors.kilometersDriven?.toLocaleString('en-IN')} km</span>
                </div>
                <div className="factor-item">
                  <span className="factor-label">Km Factor</span>
                  <span className="factor-value">{valuation.factors.kmFactor}x</span>
                </div>
                <div className="factor-item">
                  <span className="factor-label">Location Factor</span>
                  <span className="factor-value">{valuation.factors.locationFactor}x</span>
                </div>
                <div className="factor-item">
                  <span className="factor-label">Owner</span>
                  <span className="factor-value">{valuation.factors.ownerNumber}{getOrdinalSuffix(valuation.factors.ownerNumber)}</span>
                </div>
              </div>

              <div className="disclaimer">
                <strong>Disclaimer:</strong> These are estimated values based on standard depreciation rates and market factors.
                Actual resale value may vary based on vehicle condition, service history, and market demand.
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .valuation-container {
          display: grid;
          grid-template-columns: 1fr 1.2fr;
          gap: 24px;
          margin-top: 24px;
        }

        @media (max-width: 1024px) {
          .valuation-container {
            grid-template-columns: 1fr;
          }
        }

        .valuation-form-card,
        .valuation-result-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .valuation-form-card h3,
        .valuation-result-card h3 {
          margin: 0 0 20px 0;
          font-size: 18px;
          font-weight: 600;
          color: #333;
          padding-bottom: 12px;
          border-bottom: 2px solid #f0f0f0;
        }

        .vehicle-type-toggle {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
        }

        .toggle-btn {
          flex: 1;
          padding: 12px 20px;
          border: 2px solid #e0e0e0;
          background: white;
          border-radius: 8px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .toggle-btn:hover {
          border-color: #667eea;
        }

        .toggle-btn.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-color: transparent;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #555;
          font-size: 14px;
        }

        .form-group select,
        .form-group input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s;
        }

        .form-group select:focus,
        .form-group input:focus {
          outline: none;
          border-color: #667eea;
        }

        .form-group select:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
        }

        .form-hint {
          display: block;
          margin-top: 4px;
          font-size: 12px;
          color: #888;
        }

        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .btn {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          flex: 1;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: #f0f0f0;
          color: #333;
        }

        .btn-secondary:hover {
          background: #e0e0e0;
        }

        .empty-result {
          text-align: center;
          padding: 40px 20px;
          color: #888;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
          font-weight: bold;
          color: #667eea;
        }

        .calculating {
          text-align: center;
          padding: 40px 20px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #f0f0f0;
          border-top-color: #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .result-content {
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .condition-prices-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        @media (max-width: 768px) {
          .condition-prices-grid {
            grid-template-columns: 1fr;
          }
        }

        .condition-card {
          padding: 16px;
          border-radius: 12px;
          border: 2px solid;
          transition: transform 0.2s;
        }

        .condition-card:hover {
          transform: translateY(-2px);
        }

        .condition-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .condition-label {
          font-weight: 600;
          font-size: 14px;
        }

        .condition-factor {
          font-size: 12px;
          font-weight: 500;
          opacity: 0.8;
        }

        .condition-price {
          font-size: 24px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .condition-range {
          font-size: 12px;
          color: #6b7280;
          margin-bottom: 8px;
        }

        .condition-desc {
          font-size: 11px;
          color: #9ca3af;
        }

        .depreciation-info {
          margin-bottom: 20px;
        }

        .depreciation-bar {
          height: 8px;
          background: #f0f0f0;
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 8px;
        }

        .depreciation-fill {
          height: 100%;
          background: linear-gradient(90deg, #f59e0b, #ef4444);
          border-radius: 4px;
          transition: width 0.5s ease;
        }

        .depreciation-text {
          font-size: 13px;
          color: #666;
        }

        .factors-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }

        @media (max-width: 768px) {
          .factors-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .factor-item {
          padding: 12px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .factor-label {
          display: block;
          font-size: 12px;
          color: #888;
          margin-bottom: 4px;
        }

        .factor-value {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #333;
        }

        .disclaimer {
          font-size: 12px;
          color: #888;
          padding: 12px;
          background: #fff8e1;
          border-radius: 8px;
          border-left: 3px solid #f59e0b;
        }

        .disclaimer strong {
          color: #333;
        }

        .alert-danger {
          background: #fee2e2;
          color: #dc2626;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }

        .page-subtitle {
          color: #666;
          margin-top: 4px;
        }
      `}</style>
    </div>
  );
}

// Helper function for ordinal suffix
function getOrdinalSuffix(num) {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

export default Valuation;
